import { Button, ButtonGroup, Icon, Popover, Skeleton, styled } from '@moleculeone/ui';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  convertMolfileToMol,
  convertSmilesToMol,
  getMolByName,
  resetErrorMolByName,
  resetErrorMolfileToMol,
  resetErrorSmilesToMol,
} from '../../utils/molecule-util/molecule-util.actions';
import { CompoundEditorActions } from '../../hooks/useCompoundEditor';

import { CompoundPreview } from './components/CompoundPreview';

const Form = styled('form', {
  position: 'relative',
  display: 'flex',
  gap: '$3',
});
const Input = styled('input', {
  'border': 'none',
  'borderRadius': '$normal 0 0 $normal',
  'background': '$slate2',
  'padding': '$3 $5 $3 $7',
  'width': '100%',
  '&:focus': {
    outline: 'none',
  },
});
const InputIcon = styled(Icon, {
  pointerEvents: 'none',
  position: 'absolute',
  top: '$3',
  left: '0',
});
const CompoundContainer = styled('div', {
  marginTop: '$5',
});
const Info = styled('p', {
  marginTop: '$4',
  marginBottom: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  fontSize: '$2',
  fontWeight: 300,
  color: '$slate10',
});
const ErrorMessage = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
  marginTop: '$3',
  padding: '$3 $4',
  backgroundColor: '$slate4',
  color: '$red9',
  borderRadius: '$small',
});

type SearchTypeName = 'smiles' | 'name' | 'molfile';
type SearchType = {
  name: SearchTypeName;
  label: string;
  placeholder: string;
  action: (input: string) => void;
};
const searchTypes: SearchType[] = [
  {
    name: 'smiles',
    label: 'SMILES',
    placeholder: 'O=C(O)C',
    action: convertSmilesToMol,
  },
  {
    name: 'name',
    label: 'Name / CAS',
    placeholder: 'benzene',
    action: getMolByName,
  },
  {
    name: 'molfile',
    label: 'Molfile',
    placeholder: 'Paste in the molfile contents',
    action: convertMolfileToMol,
  },
];

type InsertCompoundProps = {
  onPickAtom: CompoundEditorActions['onFragmentAtomMode'];
  onPickBond: CompoundEditorActions['onFragmentBondMode'];
};
export const InsertCompound = ({ onPickAtom, onPickBond }: InsertCompoundProps) => {
  const [open, setOpen] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>(searchTypes[0]);
  const [searchInput, setSearchInput] = useState('');

  const dispatch = useDispatch();

  const { mols, isFetching, errors } = useSelector((state: any) => {
    const { molsBySmiles, molsByName, molsByMolfiles, processing, error } = state.moleculeUtil;
    return {
      mols: {
        smiles: molsBySmiles,
        name: molsByName,
        molfile: molsByMolfiles,
      },
      errors: {
        smiles: error.smilesToMol,
        name: error.nameToMol,
        molfile: error.molfileToMol,
      },
      isFetching: processing > 0,
    };
  });

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchInput) {
      dispatch(searchType.action(searchInput));
    }
  };

  const resetErrors = () => {
    dispatch(resetErrorSmilesToMol());
    dispatch(resetErrorMolfileToMol());
    dispatch(resetErrorMolByName());
  };

  const possibleMol = searchInput && mols[searchType.name][searchInput];

  return (
    <Popover
      hasArrow
      trigger={<Button theme="purple" weight="outlined" text="Import" icon="input" size="small" />}
      contentProps={{
        sideOffset: 5,
        side: 'bottom',
        css: {
          width: 'auto',
          backgroundColor: '$slate2',
          padding: '$6 $8',
        },
      }}
      open={open}
      onOpenChange={open => {
        setOpen(open);
      }}
    >
      <Form onSubmit={onFormSubmit}>
        <Input
          type="text"
          autoFocus={open}
          name="insertMolecule"
          placeholder={searchType.placeholder}
          value={searchInput}
          onChange={event => {
            resetErrors();
            setSearchInput(event.target.value);
          }}
        />
        <InputIcon icon="search" size="small" />
        <ButtonGroup direction="row">
          {searchTypes.map(type => (
            <Button
              key={type.name}
              type="button"
              weight="minimal"
              active={searchType.name === type.name}
              text={type.label}
              size="small"
              onClick={() => {
                resetErrors();
                setSearchType(type);
              }}
              css={{ whiteSpace: 'nowrap' }}
            />
          ))}
        </ButtonGroup>
        <Button
          type="submit"
          weight="solid"
          color="indigo-purple"
          icon="search"
          size="small"
          css={{ fontSize: '$4' }}
        />
      </Form>

      {isFetching ? (
        <Skeleton css={{ height: 200, width: '100%', margin: '$4 0' }} />
      ) : (
        possibleMol && (
          <CompoundContainer>
            <CompoundPreview
              mol={possibleMol}
              onMouseDown={(_, molEvent) => {
                if (molEvent.atom) {
                  onPickAtom(possibleMol, molEvent.idx);
                  setOpen(false);
                } else if (molEvent.bond) {
                  onPickBond(possibleMol, molEvent.idx);
                  setOpen(false);
                }
              }}
              renderDelay={60}
              size={200}
            />
            <Info>
              <Icon icon="info" /> Select atom or bond to insert by{' '}
            </Info>
          </CompoundContainer>
        )
      )}

      {searchType.name === 'smiles' && errors.smiles && (
        <ErrorMessage>
          <Icon icon="error" /> Failed to parse SMILES
        </ErrorMessage>
      )}
      {searchType.name === 'name' && errors.name && (
        <ErrorMessage>
          <Icon icon="error" /> Failed to find the compound
        </ErrorMessage>
      )}
      {searchType.name === 'molfile' && errors.molfile && (
        <ErrorMessage>
          <Icon icon="error" /> Failed to parse Molfile
        </ErrorMessage>
      )}
    </Popover>
  );
};
